import React from 'react';

import { get as getConstants } from '@lumapps/constants';
import { AppId } from '@lumapps/constants/app';
import { listAvailableConnectors, ListAvailableConnectorsParams } from '@lumapps/integrations/api';
import { Connector } from '@lumapps/integrations/types';
import { useOAuthPopup } from '@lumapps/lumx-auth/hooks/useOAuthPopup';
import { useServerList, UseServerList } from '@lumapps/lumx-hooks/hooks/useServerList';
import { GRANT_TYPE } from '@lumapps/marketplace/sdk';
import { sendWebviewMobileMessage } from '@lumapps/mobile-webview/message-emit';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { GLOBAL } from '@lumapps/translations';
import { BaseLoadingStatus, isLoading } from '@lumapps/utils/types/BaseLoadingStatus';

import { CLOSE_CONNECTION_WINDOW_KEY, CONNECTION_WINDOW_OPTIONS } from '../constants';
import { DisconnectConfirmDialog } from '../types';
import { useAuthorizeConnector } from './useAuthorizeConnector';
import { useDeauthorizeConnector } from './useDeauthorizeConnector';
import { UseProviderList, useProviderList } from './useProviderList';

export interface UseConnectorList
    extends Pick<
            UseServerList<Connector, ListAvailableConnectorsParams>,
            'hasMoreResults' | 'fetchMore' | 'isLoadingMore'
        >,
        Pick<UseProviderList, 'getProvider'> {
    /** List of connectors */
    connectors?: Connector[];
    /** State of the disconnect confirmation dialog */
    disconnectConfirmDialog: DisconnectConfirmDialog;
    /** Indicates whether a connector is currently connecting */
    isConnecting: boolean;
    /** Indicates whether a connector is currently disconnecting */
    isDisconnecting: boolean;
    /** whether the connectors/providers are loading or not */
    isLoading: boolean;
    /** Callback function used when disconnection is confirmed */
    confirmDisconnectDialogCallback: (confirmed: boolean) => void;
    /** Callback function used to handle disconnection of a connector */
    handleConnectorDisconnect: (connector: Connector) => void;
    /** Callback function used to handle the connection of a connector */
    handleConnectorConnect: (connector: Connector) => void;
    /** Function to set the state of the disconnect confirmation dialog */
    setDisconnectConfirmDialog: React.Dispatch<React.SetStateAction<DisconnectConfirmDialog>>;
}

const { applicationId } = getConstants();
const isWebviewApp = applicationId === AppId.webview;

/**
 * Hook to handle the display of the list of connectors.
 * @param {Boolean} initFetchConnectors - Wether to retrieve the list of connectors at init.
 * @param {Function} closeAuthWindowCallback - Callback to be executed when the `authorizationWindow` is closed
 */
export const useConnectorList = ({
    initFetchConnectors = true,
    closeAuthWindowCallback = undefined,
}: {
    initFetchConnectors?: boolean;
    closeAuthWindowCallback?: () => void;
} = {}): UseConnectorList => {
    const { error } = useNotification();
    const [disconnectConfirmDialog, setDisconnectConfirmDialog] = React.useState<DisconnectConfirmDialog>({
        isOpen: false,
    });

    const { status: providerListStatus, getProvider } = useProviderList();

    /**
     * Fetches the list of connectors.
     */
    const connectorList = useServerList<Connector, ListAvailableConnectorsParams>({
        params: { grantType: GRANT_TYPE.authorization_code },
        onFetch: listAvailableConnectors,
    });

    /**
     * Retrieve the list of connectors at init.
     */
    React.useEffect(() => {
        if (connectorList.status === BaseLoadingStatus.initial && initFetchConnectors) {
            connectorList.fetch();
        }
    }, [connectorList, initFetchConnectors]);

    /**
     * Fetches the authorization URL for a connector.
     */
    const authorizeUrlMutation = useAuthorizeConnector();

    /**
     * Callback function to close the authorization window.
     */
    const handleCloseAuthorizationWindow = () => {
        authorizeUrlMutation.reset();
        // Refetch connector list after a connection
        if (initFetchConnectors) {
            connectorList.fetch();
        }
        if (closeAuthWindowCallback) {
            closeAuthWindowCallback();
        }
    };

    const { openPopup, isOpen: isAuthWindowOpen } = useOAuthPopup({
        closeKey: CLOSE_CONNECTION_WINDOW_KEY,
        features: CONNECTION_WINDOW_OPTIONS,
    });

    /**
     * Click handler for connecting a connector.
     *
     * @param {Connector} connector - The connector to connect.
     */
    const handleConnectorConnect = (connector: Connector) => {
        authorizeUrlMutation.mutate(connector.id, {
            onSuccess: (data) => {
                const url = data?.link;
                if (!url) {
                    error({ translate: GLOBAL.GENERIC_ERROR });
                    return;
                }

                if (isWebviewApp) {
                    sendWebviewMobileMessage({
                        type: 'redirect',
                        payload: { type: 'connector-card', connectorId: connector.id, url },
                    });
                    return;
                }

                // The browser blocked the popup — surface an error instead of failing silently.
                if (!openPopup(url, handleCloseAuthorizationWindow)) {
                    error({ translate: GLOBAL.GENERIC_ERROR });
                }
            },
            onError: () => {
                error({ translate: GLOBAL.GENERIC_ERROR });
            },
        });
    };

    /**
     * To deauthorize a connector.
     */
    const deauthorizeMutation = useDeauthorizeConnector();

    /**
     * Disconnect click handler.
     *
     * @param {Connector} connector
     */
    const handleConnectorDisconnect = (connector: Connector) => {
        setDisconnectConfirmDialog({ connector, isOpen: true });
    };

    /**
     * Confirm diconnection dialog callback.
     *
     * @param {boolean} confirmed
     */
    const confirmDisconnectDialogCallback = React.useCallback(
        (confirmed: boolean) => {
            if (confirmed && disconnectConfirmDialog.connector) {
                deauthorizeMutation.mutate(disconnectConfirmDialog.connector.id, {
                    onSuccess: () => {
                        connectorList.fetch();
                    },
                });
            }
            setDisconnectConfirmDialog({ isOpen: false });
        },
        [connectorList, deauthorizeMutation, disconnectConfirmDialog.connector],
    );

    return {
        connectors: connectorList.items,
        disconnectConfirmDialog,
        hasMoreResults: connectorList.hasMoreResults,
        isConnecting: authorizeUrlMutation.isLoading || isAuthWindowOpen,
        isDisconnecting: deauthorizeMutation.isLoading,
        isLoading: isLoading(providerListStatus) || isLoading(connectorList.status),
        isLoadingMore: connectorList.isLoadingMore,
        confirmDisconnectDialogCallback,
        fetchMore: connectorList.fetchMore,
        getProvider,
        handleConnectorConnect,
        handleConnectorDisconnect,
        setDisconnectConfirmDialog,
    };
};
